import type { LatencyStats, MicrovmFleet, MicrovmFleetEvent, MicrovmFleetVm, MicrovmState, OpsSnapshot } from '@/hooks/useOpsSnapshot';
import type { StatusTone } from '@/lib/runTelemetry';

export const MICROVM_STATE_LABELS: Record<MicrovmState, string> = {
  PENDING: 'Starting',
  RUNNING: 'Running',
  SUSPENDING: 'Suspending',
  SUSPENDED: 'Suspended',
  TERMINATING: 'Terminating',
  TERMINATED: 'Terminated',
};

export function microvmStateTone(state: MicrovmState): StatusTone {
  switch (state) {
    case 'RUNNING':
      return 'success';
    case 'PENDING':
    case 'SUSPENDING':
      return 'active';
    case 'SUSPENDED':
      return 'neutral';
    default:
      return 'warning';
  }
}

export const LIFECYCLE_LABELS: Record<string, string> = {
  spawned: 'Spawned',
  running: 'Running',
  ready: 'Workspace ready',
  resumed: 'Resumed',
  suspended: 'Suspended',
  idle_suspend: 'Idle suspend',
  terminated: 'Terminated',
  failed: 'Claim failed',
  orphan_terminated: 'Orphan terminated',
  retired: 'Retired',
};

const RETIREMENT_LABELS: Record<string, string> = {
  microvm_lifetime: 'lifetime cap',
  microvm_suspended_age: 'asleep too long',
  microvm_suspended_cap: 'too many asleep',
  image_outdated: 'image update',
  provider_switch: 'provider switch',
};

export function retirementLabel(reason: string): string {
  return RETIREMENT_LABELS[reason] ?? reason;
}

export function lifecycleLabel(type: string): string {
  return LIFECYCLE_LABELS[type] ?? type;
}

export function lifecycleTone(type: string): StatusTone {
  switch (type) {
    case 'ready':
    case 'running':
    case 'resumed':
      return 'success';
    case 'spawned':
      return 'active';
    case 'failed':
    case 'orphan_terminated':
      return 'danger';
    case 'terminated':
      return 'warning';
    default:
      return 'neutral';
  }
}

/** A compact age such as `42s`, `7m`, `2h 5m`; `—` for an unknown or future time. */
export function formatAge(iso: string | undefined, now: number): string {
  const at = iso ? Date.parse(iso) : NaN;
  if (!Number.isFinite(at) || at > now + 1_000) return '—';
  return formatSpan(now - at);
}

/** Running time as whole minutes, the unit the platform is billed and compared in. */
export function formatMinutes(ms: number): string {
  if (!Number.isFinite(ms)) return '—';
  return `${Math.round(Math.max(0, ms) / 60_000).toLocaleString('en-US')} min`;
}

export function formatSpan(ms: number): string {
  if (!Number.isFinite(ms)) return '—';
  const seconds = Math.max(0, Math.floor(ms / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours}h ${rest}m` : `${hours}h`;
}

/** Platform lifetime left before Lambda ends the VM itself; undefined when its start or cap is unknown. */
export function lifetimeLeftMs(vm: Pick<MicrovmFleetVm, 'startedAt' | 'maximumDurationSeconds'>, now: number): number | undefined {
  const started = vm.startedAt ? Date.parse(vm.startedAt) : NaN;
  if (!Number.isFinite(started) || typeof vm.maximumDurationSeconds !== 'number') return undefined;
  return Math.max(0, started + vm.maximumDurationSeconds * 1000 - now);
}

/** The admin session page for a VM's workspace, when the VM is owned by one. */
export function sessionPathFor(target: Pick<MicrovmFleetVm | MicrovmFleetEvent, 'organizationId' | 'sessionId'>): string | undefined {
  if (!target.organizationId || !target.sessionId) return undefined;
  return `/organizations/${encodeURIComponent(target.organizationId)}/sessions/${encodeURIComponent(target.sessionId)}`;
}

export function runPathFor(target: Pick<MicrovmFleetEvent, 'organizationId' | 'runId'>): string | undefined {
  if (!target.organizationId || !target.runId) return undefined;
  return `/organizations/${encodeURIComponent(target.organizationId)}/runs/${encodeURIComponent(target.runId)}`;
}

/** The snapshot file name, which carries its content hash; the bucket path is the same for every VM. */
export function snapshotLabel(uri: string): string {
  const name = uri.split('/').pop() ?? uri;
  return name.length > 16 ? `${name.slice(0, 12)}…` : name;
}

/** The version new VMs launch from: the pinned one, else the latest ACTIVE one. */
export function currentImageVersion(fleet: Pick<MicrovmFleet, 'image'>): string | undefined {
  return fleet.image.pinnedVersion ?? fleet.image.activeVersion;
}

export function isLiveVm(vm: Pick<MicrovmFleetVm, 'state'>): boolean {
  return vm.state !== 'TERMINATING' && vm.state !== 'TERMINATED';
}

/** A live VM on a version other than the one new VMs launch from; false when either version is unknown. */
export function isOnOldImage(vm: Pick<MicrovmFleetVm, 'state' | 'imageVersion'>, current: string | undefined): boolean {
  return Boolean(current && vm.imageVersion && vm.imageVersion !== current && isLiveVm(vm));
}

export type MicrovmFilter = 'live' | 'all' | 'orphans' | 'outdated';
export function filterVms(vms: MicrovmFleetVm[], filter: MicrovmFilter, current?: string): MicrovmFleetVm[] {
  if (filter === 'orphans') return vms.filter((vm) => !vm.owned);
  if (filter === 'live') return vms.filter(isLiveVm);
  if (filter === 'outdated') return vms.filter((vm) => isOnOldImage(vm, current));
  return vms;
}

export const MICROVM_PROVIDER = 'lambda-microvm';

const PREVIEW_PATH_LABELS: Record<string, string> = {
  cold: 'Cold start',
  'parked-restore': 'Resume from snapshot',
  warm: 'Warm',
  reserve: 'Reserve',
};

export function previewPathLabel(path: string): string {
  return PREVIEW_PATH_LABELS[path] ?? humanize(path);
}

const PROVIDER_LABELS: Record<string, string> = {
  [MICROVM_PROVIDER]: 'MicroVM',
  unknown: 'Unknown lane',
};

export function providerLabel(provider: string | undefined): string {
  return PROVIDER_LABELS[provider || 'unknown'] ?? humanize(provider || 'unknown');
}

function humanize(key: string): string {
  const words = key.replace(/[-_]+/g, ' ').trim();
  return words ? words[0]!.toUpperCase() + words.slice(1) : key;
}

/**
 * Open→preview latency for MicroVM workspaces, busiest path first. An API that
 * predates the per-provider split only has the blended numbers, so those stand in.
 */
export function microvmPreviewLatency(sandbox: OpsSnapshot['sandbox']): Array<[string, LatencyStats]> {
  const byPath = sandbox.openToPreviewByProvider ? (sandbox.openToPreviewByProvider[MICROVM_PROVIDER] ?? {}) : sandbox.openToPreview;
  return Object.entries(byPath).sort((a, b) => b[1].samples - a[1].samples);
}

/** Newest first, capped. */
export function recentFailures(sandbox: OpsSnapshot['sandbox'], limit = 10): OpsSnapshot['sandbox']['recentFailures'] {
  return [...sandbox.recentFailures].sort((a, b) => b.at.localeCompare(a.at)).slice(0, limit);
}

/** Tenants with the most active workspaces first; `atLimit` flags a tenant that cannot open another. */
export function busiestTenants(sandbox: OpsSnapshot['sandbox'], limit = 10) {
  return [...sandbox.tenants]
    .sort((a, b) => b.active - a.active)
    .slice(0, limit)
    .map((tenant) => ({ ...tenant, atLimit: tenant.limit !== null && tenant.active >= tenant.limit }));
}
